import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../UI/sheet';
import { LucideHistory } from 'lucide-react';
import { Button } from '../ui/button';
import { Loader } from '../../ui_components/loader';
import moment from 'moment';
import { getEstimateHistory } from '../../api/estimate-history';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip-shadcn';
import { useElementSize } from '../../helper/customHooks';

type EstimateHistoryProps = {
  dotMenu?: boolean;
  estimateId?: any;
  singleUser?: boolean;
};

const EstimateHistory = (props: EstimateHistoryProps) => {
  const { dotMenu = false, estimateId, singleUser } = props;
  const { ref: updateHistoryHeaderRef, size } = useElementSize<HTMLDivElement>();

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [history, setHistory] = useState<any[]>([]);
  const [estimate, setEstimate] = useState<any>(null);

  const handleLoading = (val: boolean) => {
    setLoading(val);
  };

  const handleFetchHistory = async (id?: number) => {
    if (!id) {
      console.error('Estimate ID is missing.');
      toast.error('Estimate ID is missing.');
      return;
    }

    const res = await getEstimateHistory(id);
    if (res.success) {
      setHistory(res.estimateHistory || []);
      setEstimate(res.estimate || null);
      handleLoading(false);
    } else {
      toast.error('Error while getting estimate history.');
    }
  };

  useEffect(() => {
    if (open) {
      handleFetchHistory(estimateId);
    }

    return () => {};
  }, [open]);

  // const renderHistoryAction = (action: any) => {
  //   if (!action) return '';

  //   switch (action) {
  //     case 'created':
  //     case 'updated':
  //     case 'saved':
  //       return 'saved by';
  //     case 'sent_and_saved':
  //       return 'sent/saved by';
  //     case 'deleted':
  //       return 'deleted by';
  //     case 'status_changed':
  //       return 'status changed by';
  //     case 'exported_to_qb':
  //       return 'exported by';
  //     default:
  //       return '';
  //   }
  // };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {dotMenu ? (
          <div className="hover:bg-accent hover:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:hover:bg-destructive/10 dark:data-[variant=destructive]:hover:bg-destructive/20 data-[variant=destructive]:hover:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
            <span className="inline-flex items-center gap-2">
              <LucideHistory className="h-4 w-4" />
              History
            </span>
          </div>
        ) : (
          <Button
            type="button"
            variant={'outline'}
            className={`min-h-[32px] max-h-[32px] font-medium py-0 px-[12px] text-[12px]`}
            onClick={() => {}}
          >
            <LucideHistory className="h-4 w-4" />
            History
          </Button>
        )}
      </SheetTrigger>

      <SheetContent className="sm:max-w-[420px]">
        <div ref={updateHistoryHeaderRef}>
          <SheetHeader className="px-[24px] py-[16px]">
            <SheetTitle className="text-[20px]">Update History</SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground wrap-text">
              Estimate {estimate?.estimateBody?.baseNumber || ''}
            </SheetDescription>
          </SheetHeader>
        </div>

        {loading ? (
          <div className="relative h-full">
            <Loader className="h-full" />
          </div>
        ) : (
          <div
            className="flex-1 overflow-y-auto px-6 py-4"
            style={{ maxHeight: `calc(100% - ${size?.height}px)` }}
          >
            <div className="space-y-1">
              <div className="divide-y divide-border">
                {history?.map((item: any, index: number) => {
                  return (
                    <div key={index} className="py-3 first:pt-0">
                      <div className="flex justify-between gap-3">
                        <div className="flex gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {item?.versionedNumber || ''}
                          </span>

                          {index === 0 && (
                            <div className="inline-flex items-center rounded-full border transition-colors hover:outline-none hover:ring-2 hover:ring-ring hover:ring-offset-2 hover:bg-secondary/80 text-[10px] px-1.5 py-0 h-4 font-medium bg-status-success/15 text-status-success border-transparent">
                              Current
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 shrink-0">
                          {!singleUser && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="leading-normal text-primary font-medium text-xs">
                                  @{item?.userLabel}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="z-[99999990]">
                                {item?.user
                                  ? `${item?.user?.firstName} ${item?.user?.lastName}`
                                  : null}
                              </TooltipContent>
                            </Tooltip>
                          )}

                          <span className="leading-normal text-xs text-muted-foreground">
                            {moment(item.createdAt).format('MMM D, YYYY [at] h:mm A')}
                          </span>
                        </div>
                      </div>

                      {item?.internalNote ? (
                        <p className="m-0 mt-1 text-sm text-muted-foreground wrap-text whitespace-pre-wrap break-words">
                          {item.internalNote}
                        </p>
                      ) : (
                        <p className="m-0 mt-1 text-xs text-muted-foreground/60 italic">No note</p>
                      )}

                      {item?.qbEstimateNumber && (
                        <p className="m-0 mt-1 text-sm text-muted-foreground wrap-text whitespace-pre-wrap break-words">
                          QBO: Est #{item?.qbEstimateNumber}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default EstimateHistory;
